import { yupResolver } from "@hookform/resolvers/yup";
import React, { useState } from "react";
import { FormProvider, useForm, Controller } from "react-hook-form";
import httpClient from "services/network/httpClient";
import Input from "components/Input";
import SimpleSelect, { } from "components/SimpleSelect";
import PillButton from "components/button/Pills";
import { showToast } from "components/Toaster";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/style.css";
import { ValidationSchemas } from "utility/validationUtils";
import * as yup from "yup";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faUser,
  faEnvelope,
  faCity,
  faVenusMars,
  faMapMarkerAlt,
  faMapPin,
  faIdBadge,
  faBriefcase,
} from "@fortawesome/free-solid-svg-icons";
import CustomSelect from "components/Select";
import { getDarkSelectStyles } from "pages/summary/demo/DemoReportFilter";

export interface UserProps {
  id: string;
  firstName: string;
  email: string;
  city: string;
  mobile: string;
  state: string;
  postalCode: string;
  gender: string;
  address: string;
  employeeCode: string;
  designation: string;
  status: string;
  reportingTo: string;
}

// Using the SelectOption interface from SimpleSelect component
// No need for separate UserTypes interface

interface UpdateProps {
  upDatedData: UserProps;
  onClose: () => void;
  getData: () => void;
  emailData: any[];
}
const normalizeMobileForApi = (value: string) => {
  if (!value) return value;
  const digits = value.replace(/\D/g, "");   // keep only numbers
  return `+${digits}`;                       // ensure + prefix
};
// Use our validation schema and extend it with the status field
const validationSchema = yup.object().shape({
  firstName: ValidationSchemas.string('Employee Name'),
  employeeCode: yup.string().trim().required("Employee code is required"),
  email: ValidationSchemas.email(),
  city: ValidationSchemas.string('City'),
  mobile: yup.string().required("Mobile number is required")
    .test("is-ten-digits", "Mobile number must be exactly 10 digits", (value) => {
      if (!value) return false;
      const digits = value.replace(/\D/g, ""); // "+91 9876543210" → "919876543210"
      // If starts with country code "91", drop it
      const cleaned = digits.startsWith("91") ? digits.slice(2) : digits;
      return cleaned.length === 10;
    }),
  state: ValidationSchemas.string('State'),
  postalCode: ValidationSchemas.postalCode(),
  gender: ValidationSchemas.string('Gender'),
  address: ValidationSchemas.string('Address'),
  designation: ValidationSchemas.string('Designation'),
  // employeeCode: ValidationSchemas.string('Employee Code', false),
  reportingToId: ValidationSchemas.number('Reporting Manager', false),
  status: ValidationSchemas.string('Status')
});

const UpdateEmployee = ({
  upDatedData,
  onClose,
  getData,
  emailData,
}: UpdateProps) => {
  const methods = useForm({
    resolver: yupResolver(validationSchema),
    defaultValues: {
      ...upDatedData,
      // Ensure reportingToId is properly set if available
      reportingToId: upDatedData.reportingTo ?
        emailData.find(item => item.label === upDatedData.reportingTo)?.value :
        undefined
    },
  });

  const [isDarkMode] = useState(
    () => document.documentElement.classList.contains("dark")
  );

  // We don't need to track and log form errors on every render
  // This was likely contributing to the infinite loop issue


  const submit = (data: any) => {
    console.log("Form submission triggered with data:", data);

    // Validate form data
    if (Object.keys(methods.formState.errors).length > 0) {
      // Check specifically for email errors
      if (methods.formState.errors.email) {
        showToast(methods.formState.errors.email.message as string || "Please enter a valid email address", "error");
      } else {
        console.error("Form has validation errors:", methods.formState.errors);
      }
      return;
    }


    const payload = {
      ...data,
      mobile: normalizeMobileForApi(data.mobile),
      id: upDatedData.id, // Make sure ID is included
    };
    console.log("Sending API request with payload:", payload);

    // Add a debugger for network inspection if needed
    // debugger;

    httpClient
      .put(`employees/update`, payload)
      .then((response: any) => {
        console.log("Update Employee response:", response);

        if (response.success === true) {
          showToast("Employee Details Updated Successfully", "success");
          onClose();
          getData();
        } else {
          const errorMessage =
            response?.error?.message ||
            "Failed to update employee";

          showToast(String(errorMessage), "error");
          console.log("233", errorMessage);
        }
      })
      .catch((error) => {
        console.error("API error:", error);
        // Check various error properties
        const errorMessage =
          error?.response?.data?.message ||
          error?.response?.message ||
          error?.message ||
          "An error occurred during update";
        showToast(errorMessage, "error");
        console.log("123", errorMessage);
      });
  };



  // Use useMemo to create stable references for options arrays
  const userStatus = React.useMemo(() => [
    {
      value: "ACTIVE",
      label: "ACTIVE",
    },
    {
      value: "INACTIVE",
      label: "INACTIVE",
    },
  ], []);

  //const data1: SelectOption[] = React.useMemo(() => emailData, [emailData]);

  // Create stable defaultValue objects using useMemo
  // const reportingToDefaultValue = React.useMemo(() => {
  //   return data1?.find(option => option.label === upDatedData.reportingTo) || null;
  // }, [data1, upDatedData.reportingTo]);

  const statusDefaultValue = React.useMemo(() => {
    return userStatus?.find(option => option.label === upDatedData.status) || null;
  }, [userStatus, upDatedData.status]);
  return (
    <FormProvider {...methods}>
      <form onSubmit={methods.handleSubmit(submit)}>
        <div className="grid grid-cols-3 gap-3 p-3">
          <Input
            name={"firstName"}
            label={"Employee Name"}
            placeholder="Enter Employee Name"
            icon={<FontAwesomeIcon icon={faUser} className="text-gray-400" />}
            required
            onlyLetters
          />

          <Input
            name="employeeCode"
            label="Employee Code"
            placeholder="Enter Employee Code"
            icon={<FontAwesomeIcon icon={faUser} className="text-gray-400" />}
            required
          />
          <Input
            name={"email"}
            label={"Email"}
            placeholder="Enter Employee Email (example@domain.com)"
            icon={<FontAwesomeIcon icon={faEnvelope} className="text-gray-400" />}
            noCapitalize={true}
            autoComplete="email"
            required
            helperText={methods.formState.errors.email ? methods.formState.errors.email.message as string : ""}
          // Remove the onChange handler that triggers validation on every keystroke
          />
          <div className="mb-3">
            <label htmlFor="mobile" className="block mb-1 text-sm font-medium text-gray-900 dark:text-gray-200">
              Mobile Number <span className="text-red-500">*</span>
            </label>

            {/* --- CHANGE: PhoneInput connected to RHF --- */}
            <Controller
              name="mobile"
              control={methods.control}
              rules={{
                required: "Mobile number is required",
                validate: (v) => {
                  if (!v) return "Mobile number is required";

                  const digits = v.replace(/\D/g, "");
                  const cleaned = digits.startsWith("91") ? digits.slice(2) : digits;

                  return cleaned.length === 10
                    ? true
                    : "Mobile number must be exactly 10 digits";
                },
              }}
              render={({ field }) => (
                <div className="relative rounded-lg border border-gray-300">
                  <PhoneInput
                    {...field}
                    country="in"
                    value={field.value}
                    onChange={(value) => field.onChange(value)}
                    //In dark mode input field color
                    inputClass="!w-full !bg-white  !text-[#374151]  !border-none  dark:!bg-gray-800  dark:!text-white [&>input]:!text-[#374151]
  dark:[&>input]:!text-white"
                    buttonClass="!bg-white dark:!bg-gray-700"
                    dropdownClass="
  !bg-white !text-[#374151] 
  dark:!bg-gray-800 dark:!text-white

  [&>.country:hover]:!bg-gray-100 
  [&>.country:hover]:!text-black
  dark:[&>.country:hover]:!bg-gray-700 
  dark:[&>.country:hover]:!text-white

  [&>.country.highlight]:!bg-gray-200 
  [&>.country.highlight]:!text-black
  dark:[&>.country.highlight]:!bg-gray-700 
  dark:[&>.country.highlight]:!text-white"//text color when cursor over the dropdown
                    // ⭐ Forward RHF ref to the actual input (fixes focus crash)
                    inputProps={{
                      id: "mobile",
                      name: field.name,
                      ref: field.ref,
                      onBlur: field.onBlur,
                      required: true,
                    }}
                    searchClass="!text-black dark:!text-black !bg-white dark:!bg-gray-700" //seach field text color
                    enableSearch
                    inputStyle={{
                      width: "100%",
                      height: "38px",
                      borderRadius: "0.5rem",
                      border: "none",
                      fontSize: "14px",
                    }}
                    buttonStyle={{
                      border: "none",
                      background: "none",
                      borderRadius: "0.5rem 0 0 0.5rem",
                    }}
                  />
                </div>
              )}
            />

            {methods.formState.errors.mobile && (
              <span className="text-red-500 text-xs mt-1">
                {methods.formState.errors.mobile.message as string}
              </span>
            )}

          </div>

          <Input
            name={"designation"}
            label={"Designation"}
            placeholder="Enter Designation"
            icon={<FontAwesomeIcon icon={faBriefcase} className="text-gray-400" />}
            required
            onlyLetters
          />
          <Input
            name={"gender"}
            label={"Gender"}
            placeholder="Enter Employee Gender"
            icon={<FontAwesomeIcon icon={faVenusMars} className="text-gray-400" />}
            required
            onlyLetters
          />
          <Input
            name={"city"}
            label={"City"}
            placeholder="Enter Employee City"
            icon={<FontAwesomeIcon icon={faCity} className="text-gray-400" />}
            required
            onlyLetters
          />
          <Input
            name={"state"}
            label={"State"}
            placeholder="Enter Employee State"
            icon={<FontAwesomeIcon icon={faMapMarkerAlt} className="text-gray-400" />}
            required
            onlyLetters
          />
          {/* Reporting To */}
          <CustomSelect
            name="reportingToId"
            label="Select Reporting To"
            options={emailData}
            placeholder="Select Employee"
            size="lg"
            errorMessage={methods.formState.errors.reportingToId?.message as string}
            customStyles={getDarkSelectStyles(isDarkMode)}
          />
          <SimpleSelect
            name="status"
            label="User Status"
            options={userStatus}
            placeholder="Select User Status"
            isRequired={true}
            icon={<FontAwesomeIcon icon={faBriefcase} className="text-gray-400" />}
            defaultValue={statusDefaultValue}
            errorMessage={methods.formState.errors.status?.message as string}
            className="mb-3"
          />
          <div className="col-span-2">
            <Input
              name={"address"}
              label={"Address"}
              placeholder="Enter Employee address"
              type="textarea"
              icon={<FontAwesomeIcon icon={faMapPin} className="text-gray-400 mt-2" />}
              required
              maxLength={150}
              helperText="Max 150 chars"
            />
          </div>
          <div className="col-span-1">
            <Input
              name={"postalCode"}
              label={"Postal code"}
              placeholder="Enter 6 digit PIN code"
              type="text"
              icon={<FontAwesomeIcon icon={faIdBadge} className="text-gray-400" />}
              required
              onlyDigits
              maxLength={6}
              helperText="Must be exactly 6 digits"
              onChange={(e) => {
                // Allow only digits in postal code
                e.target.value = e.target.value.replace(/\D/g, '');
              }}
            />
          </div>
        </div>

        <div className="flex flex-row justify-end gap-4 mt-4 pr-3 pb-3">
          {/* <PillButton 
            type="button" 
            variant="primary" 
            onClick={testApiCall}
          >
            Test API
          </PillButton> */}
          <PillButton
            type="submit"
            variant="secondary"
          >
            Update
          </PillButton>
          <PillButton type="reset" onClick={onClose} variant="primary">
            Cancel
          </PillButton>
        </div>
      </form>
    </FormProvider>
  );
};
export default UpdateEmployee;
